import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  GOOGLE_MAPS_CONFIG,
  extractAddressComponents,
  loadGoogleMapsAPI,
  isGoogleMapsLoaded,
} from "../config/googleMaps";

interface AddressData {
  line1: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: AddressData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Enter your address",
  className = "",
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAutocomplete = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load Google Maps API if not already loaded
        if (!isGoogleMapsLoaded()) {
          await loadGoogleMapsAPI();
        }

        // Wait a bit for the API to be fully initialized
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (inputRef.current && !autocompleteRef.current) {
          // Create autocomplete instance
          autocompleteRef.current = new google.maps.places.Autocomplete(
            inputRef.current,
            GOOGLE_MAPS_CONFIG.autocompleteOptions
          );

          // Add listener for place selection
          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current?.getPlace();

            if (place && place.formatted_address) {
              // Update the input value
              onChange(place.formatted_address);

              // Extract address components and notify parent
              const addressData = extractAddressComponents(place);
              onAddressSelect(addressData);
            }
          });
        }
      } catch (err) {
        console.error("Error initializing Google Maps autocomplete:", err);
        setError(
          "Failed to load address autocomplete. Please enter address manually."
        );
      } finally {
        setIsLoading(false);
      }
    };

    initializeAutocomplete();

    // Cleanup function
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []); // Remove dependencies to prevent re-initialization

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onChange(value);

      // If user clears the input, clear the address data
      if (value === "") {
        onAddressSelect({
          line1: "",
          city: "",
          state: "",
          zipCode: "",
        });
      }
    },
    [onChange, onAddressSelect]
  );

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        className={`w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
          error ? "border-red-500" : ""
        } ${
          disabled || isLoading ? "bg-gray-100 cursor-not-allowed" : ""
        } ${className}`}
      />

      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
        </div>
      )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {!GOOGLE_MAPS_CONFIG.apiKey && (
        <p className="text-yellow-600 text-xs mt-1">
          ⚠️ Google Maps API key not configured. Autocomplete will not work.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
